import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { PortfolioAsset } from '../../../models/portfolio.model';
import {
	getTodayProfitLoss,
	getTotalNetSaleValue,
	getTotalProfitLoss,
	getTotalProfitLossPercent,
	hasPositiveBalance,
	withCommissionRate,
	withPrice,
	withPriceBasis
} from '../../../models/portfolio.utils';
import { mapDepositPortfolioToAsset } from '../../../models/portfolio-response.mapper';
import { CommodityAccessService } from '../../../services/commodity-access.service';
import { PortfolioService } from '../../../services/portfolio.service';
import { MarketDataStore } from '../../../core/market-data';
import { mapSymbolToClosingPrice } from '../../market-depth-best-limit/models/market-depth.model';
import { PortfolioViewStateService } from './portfolio-view-state.service';
import { symbolIcon } from '../../../models/symbol.model';

export interface PortfolioStoreState {
	assets: PortfolioAsset[];
	loading: boolean;
	error: string | null;
}

const initialState: PortfolioStoreState = {
	assets: [],
	loading: false,
	error: null
};

/**
 * Carries the prices a holding already has onto its freshly loaded self.
 *
 * `/portfolios/deposit` reports no price at all, so a reload maps every holding
 * back to a 0 price — and the root-provided store keeps the previous list on
 * screen across one (the loader only stands in on a cold load). Re-entering the
 * screen therefore showed the trader real figures, then 0 for the length of the
 * symbol snapshot round-trip, then the figures again. A price one request old is a
 * better answer than 0 for that moment, and {@link seedPrices} replaces it with a
 * fresh one either way.
 *
 * A holding the store has not seen before is left as mapped: it has no price to
 * carry, and nothing on screen to flicker.
 */
function withKnownPrice(asset: PortfolioAsset, known: PortfolioAsset | undefined): PortfolioAsset {
	if (!known) {
		return asset;
	}

	// The commission rate goes on first for the reason `seedPrices` gives: the
	// profit/loss `withPrice` computes is net of it.
	return withPrice(
		withCommissionRate({ ...asset, icon: known.icon }, known.sellCommissionRate),
		known.price,
		known.lastSettlementPrice,
		known.closingPrice
	);
}

export const PortfolioStore = signalStore(
	{ providedIn: 'root' },
	withState(initialState),
	withComputed(({ assets, loading, error }, viewState = inject(PortfolioViewStateService)) => {
		/**
		 * The holdings measured at مبنای قیمت — what every screen reads.
		 *
		 * Derived rather than written back into state, so scenario 4 needs no
		 * reload: choosing a basis, and the session opening or closing under a
		 * trader who is still on the screen, both recompute the summary and every
		 * card at once. `assets` stays the holdings as the API and the feed
		 * reported them, which is what the next tick has to be applied to.
		 */
		const valuedAssets = computed(() =>
			assets().map(asset => withPriceBasis(asset, viewState.effectivePriceBasis()))
		);

		return {
			valuedAssets,
			isEmpty: computed(() => !loading() && !error() && assets().length === 0),
			/** The aggregate figures behind the summary header, on the same basis as the cards. */
			totalNetSaleValue: computed(() => getTotalNetSaleValue(valuedAssets())),
			totalProfitLoss: computed(() => getTotalProfitLoss(valuedAssets())),
			totalProfitLossPercent: computed(() => getTotalProfitLossPercent(valuedAssets())),
			todayProfitLoss: computed(() => getTodayProfitLoss(valuedAssets()))
		};
	}),
	withMethods(
		(
			store,
			portfolioService = inject(PortfolioService),
			marketData = inject(MarketDataStore),
			commodityAccess = inject(CommodityAccessService)
		) => {
			let requestGeneration = 0;

			/**
			 * Seeds the prices `/portfolios/deposit` does not carry, so the list shows
			 * real figures on first paint rather than waiting on the first market tick.
			 *
			 * It also carries the symbol's sell commission, which `/portfolios/deposit`
			 * does not report and the profit/loss formula charges on the sale.
			 *
			 * A failure here is deliberately not surfaced: the holdings themselves
			 * loaded, and the screen is more useful with 0 prices than replaced by an
			 * error. The live feed fills them in moments later either way.
			 */
			async function seedPrices(generation: number, assets: readonly PortfolioAsset[]): Promise<void> {
				if (assets.length === 0) {
					return;
				}

				try {
					const symbols = await firstValueFrom(marketData.getSymbols(assets.map(asset => asset.symbolISIN)));
					if (generation !== requestGeneration) {
						return;
					}

					// Matched on contractId rather than isin: both sides carry it, and
					// `symbolinfo` reports the isin upper-cased while the portfolio does not
					// promise a casing.
					const prices = new Map(symbols.map(symbol => [symbol.contractId, symbol]));

					patchState(store, {
						assets: store.assets().map(asset => {
							const symbol = prices.get(asset.contractId);
							if (!symbol) {
								return asset;
							}

							// The sell commission goes on first: profit/loss is computed
							// net of it, and `withPrice` is what triggers that.
							return withPrice(
								withCommissionRate(
									{ ...asset, icon: symbolIcon(symbol.isin) },
									symbol.sellCommission ?? 0
								),
								symbol.lastTradedPrice,
								symbol.lastSettlementPrice,
								mapSymbolToClosingPrice(symbol),
								symbol.lastPriceChange
							);
						})
					});
				} catch {
					// Prices stay at 0 until the market feed delivers one.
				}
			}

			return {
				/**
				 * Loads the holdings, in two phases the caller can tell apart.
				 *
				 * It resolves the moment the holdings are in state and `loading` is off —
				 * the point the list becomes visible — and hands back a promise that
				 * settles once {@link seedPrices} has made its own symbol snapshot call. They are
				 * separate because that second call happens behind an already-interactive
				 * list: a caller that orders the cards itself (the portfolio screen does)
				 * has to order them at the first phase, or the trader reads raw API order
				 * for the length of the second request, and again at the second, where a
				 * cold load's value figures finally stop being 0.
				 */
				async loadPortfolio(): Promise<{ pricesSeeded: Promise<void> }> {
					const generation = ++requestGeneration;
					patchState(store, { loading: true, error: null });

					try {
						const holdings = await firstValueFrom(portfolioService.getDepositPortfolio());
						if (generation !== requestGeneration) {
							return { pricesSeeded: Promise.resolve() };
						}

						// Scenario 1: one card per holding with a positive balance, so
						// closed-out positions the API still returns are dropped here
						// rather than being filtered again in every consumer. Each one
						// keeps the price the store already had for it — see
						// {@link withKnownPrice}.
						const knownPrices = new Map(store.assets().map(asset => [asset.contractId, asset]));
						const assets = holdings
							.map(mapDepositPortfolioToAsset)
							.filter(hasPositiveBalance)
							.map(asset => withKnownPrice(asset, knownPrices.get(asset.contractId)));
						patchState(store, { assets, loading: false });

						return { pricesSeeded: seedPrices(generation, assets) };
					} catch {
						if (generation !== requestGeneration) {
							return { pricesSeeded: Promise.resolve() };
						}

						// The endpoint refuses a customer without a کد بورس کالا, and the holdings it
						// would have listed are ones they cannot hold — so that refusal is the list's
						// empty state rather than a failure to report.
						patchState(
							store,
							commodityAccess.denied()
								? { assets: [], loading: false, error: null }
								: { error: 'دریافت پرتفوی با خطا مواجه شد', loading: false }
						);

						return { pricesSeeded: Promise.resolve() };
					}
				},

				/**
				 * Applies one market-view tick. Holdings the tick does not name are left
				 * untouched, so the feed's all-deposits stream costs nothing extra.
				 */
				applyPriceUpdate(
					contractId: number,
					price: number,
					lastSettlementPrice: number,
					closingPrice = 0
				): void {
					patchState(store, {
						assets: store
							.assets()
							.map(asset =>
								asset.contractId === contractId
									? withPrice(asset, price, lastSettlementPrice, closingPrice)
									: asset
							)
					});
				}
			};
		}
	)
);
